<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import InputWithVariables from 'dashboard/components/widgets/conversation/WhatsappTemplates/atendeTotal/TemplateParser/InputWithVariables.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  // Zimobi: set when the template is sent into a conversation (reply box, new
  // conversation). The media header is then an uploaded file — sent as the
  // message attachment and turned into the header by the backend — and body
  // variables accept Liquid, which Message renders. Campaigns leave it off.
  forConversation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
// Zimobi: media header file, which replaces the media URL in conversations.
const headerFile = ref(null);
const headerFileInput = ref(null);
// Zimobi: blocks a second send while the first one is on its way.
const isSending = ref(false);

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

// Zimobi: upstream shows Meta's raw format ("Document"), untranslated in pt_BR.
const formatType = computed(() => {
  const mediaTypes = {
    IMAGE: t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPES.IMAGE'),
    VIDEO: t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPES.VIDEO'),
    DOCUMENT: t('WHATSAPP_TEMPLATES.PARSER.MEDIA_TYPES.DOCUMENT'),
  };
  return mediaTypes[headerComponent.value?.format];
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (
    hasMediaHeader.value &&
    !processedParams.value.header?.media_url &&
    !headerFile.value
  ) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
  headerFile.value = null;
  isSending.value = false;
};

const updateHeaderFile = event => {
  [headerFile.value] = event.target.files;
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

// Zimobi: an uploaded header goes out with the attachment's name as the document filename, so
// the name typed for a document header renames the file (keeping its extension).
const withMediaName = (file, mediaName) => {
  if (!mediaName) return file;
  const dot = file.name.lastIndexOf('.');
  const extension = dot > 0 ? file.name.slice(dot) : '';
  const name = mediaName.toLowerCase().endsWith(extension.toLowerCase())
    ? mediaName
    : `${mediaName}${extension}`;
  return new File([file], name, { type: file.type });
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid || isFormInvalid.value || isSending.value) return;

  const { name, category, language, namespace } = props.template;
  // Zimobi: an uploaded header file replaces the media URL.
  const { header, ...paramsWithoutHeader } = processedParams.value;
  const processed_params = headerFile.value
    ? paramsWithoutHeader
    : processedParams.value;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params,
    },
  };
  if (headerFile.value) {
    payload.files = [withMediaName(headerFile.value, header?.media_name)];
    payload.private = false;
  }
  isSending.value = true;
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  isSending.value = false;
  emit('resetTemplate');
};

const goBack = () => {
  isSending.value = false;
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  // Zimobi: lets the WhatsApp campaign form block submission while a variable is empty.
  isFormInvalid,
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateHeaderFile,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <!-- Zimobi: in conversations the media header is uploaded, not linked. -->
        <div v-if="!forConversation" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-else class="flex items-center gap-2 mb-2.5 min-w-0">
          <input
            ref="headerFileInput"
            type="file"
            class="hidden"
            data-testid="template-header-file"
            @change="updateHeaderFile"
          />
          <Button
            :label="t('WHATSAPP_TEMPLATES.PARSER.MEDIA_FILE_BUTTON')"
            icon="i-lucide-paperclip"
            color="slate"
            variant="faded"
            size="sm"
            data-testid="template-header-file-button"
            @click="headerFileInput.click()"
          />
          <span
            v-if="headerFile"
            data-testid="template-header-file-name"
            class="text-sm truncate text-n-slate-11"
          >
            {{ headerFile.name }}
          </span>
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <!-- Zimobi: conversations take Liquid in variables, rendered by Message.
               The editor has no placeholder, so the variable number labels it. -->
          <template v-if="forConversation">
            <span
              data-testid="template-variable-key"
              class="flex items-center h-8 text-sm min-w-6 ltr:mr-2 rtl:ml-2 text-n-slate-10"
            >
              {{ key }}
            </span>
            <InputWithVariables
              v-model="processedParams.body[key]"
              class="flex-1"
            />
          </template>
          <Input
            v-else
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid || isSending"
    />
  </div>
</template>
